/**
 * The one implementation of the inline "Show All" toggle's DISABLED state, shared by the two entity-selector
 * families that render the toggle: the select2 `MaterialsEntitySelector` tree and the pict-section-picker
 * `Picker` broker. Neither family shares a base class (one extends `PictInputExtensionProvider`, the other
 * `PictInputTypePicker`), so this module — not inheritance — is what keeps their behaviour identical, and
 * keeps `headlight-dynamic-form` and `materials-form` from drifting.
 *
 * Why: a read-only / disabled selector still painted a live-looking toggle. Clicking it wrote override state
 * and re-ran the whole filter composition for a field whose value cannot change — wasted round trips, and a
 * control that reads as actionable when nothing can come of it. Disabled fields now paint the toggle inert
 * and swallow the click.
 *
 * The disabled state is applied twice on purpose: the `{~FilterToggleDisabled:~}` macro paints the class at
 * first render (no enabled→disabled flash), and `syncToggleDisabledState` re-applies it at input-initialize
 * so a descriptor disabled AFTER render — `MaterialsSampleSelector`'s `DisabledOnceSaved` is the live case —
 * is still caught.
 */

const _SPECIALIZED_SELECTOR_MAP = require('./_SpecializedSelectorMap.js');

/** Class the templates/macro and the runtime sync both key off. */
const DISABLED_CLASS = 'hl-filter-toggle-disabled';

/** Stashes the template's own title while disabled, so the restore doesn't hardcode it. */
const TITLE_STASH_ATTRIBUTE = 'data-filter-toggle-title';

const DISABLED_TITLE = 'Show all is unavailable — this field is read-only';

/* Inert ghost-button paint. `a.button.<class>` (0,2,1) outranks Bulma's `.button.is-ghost` (0,2,0)
   regardless of injection order, which a bare `.<class>` would lose to. Pointer events stay ON so the
   explanatory title still shows on hover — the click itself is swallowed in toggleFilterOverride. */
const _FILTER_TOGGLE_DISABLED_CSS = /*css*/`
a.button.${DISABLED_CLASS},
a.button.${DISABLED_CLASS}:hover,
a.button.${DISABLED_CLASS}:focus {
	color: var(--bulma-text-weak);
	opacity: var(--bulma-button-disabled-opacity, 0.5);
	cursor: not-allowed;
	text-decoration: none;
	box-shadow: none;
}
`;

/* In a table cell the toggle floats beside the picker, and the picker's positioned host paints over a non-positioned
   float and takes its clicks; lifting the toggle into its own stacking layer keeps it clickable. */
const _FILTER_TOGGLE_TABULAR_CSS = /*css*/`
a.button.hl-tabular-filter-toggle {
	position: relative;
	z-index: 1;
}
`;

/**
 * True when the descriptor is read-only, by either flag Headlight uses: `PictForm.Disabled` (the entity
 * selectors, and the `{~Disabled:~}` macro's own source) or `PictForm.ReadOnly` (ObjectEditor / PSP).
 * @param {Record<string, any>} pInput - the input descriptor @return {boolean}
 */
const isInputDisabled = (pInput) =>
{
	const tmpPictForm = pInput && pInput.PictForm ? pInput.PictForm : null;
	if (!tmpPictForm)
	{
		return false;
	}
	return !!(tmpPictForm.Disabled || tmpPictForm.ReadOnly);
};

/**
 * Register the inert-toggle CSS with the pict CSSMap, and write it out once the page can take it.
 *
 * Registration and injection are separate on purpose. `addCSS` only ever RAISES the CSSMap's dirty flag,
 * so registering at bundle-attach time is free; `injectCSS` LOWERS it whether or not the target style
 * element exists yet, so calling it during bootstrap could quietly drop this fragment until something
 * else re-dirtied the map. Bootstrap registers (`pInjectNow` omitted); the input-initialize sync — by
 * which point the page is built — injects.
 *
 * @param {any} pPict @param {boolean} [pInjectNow] @return {boolean} true once the CSS is registered
 */
const ensureFilterToggleCSS = (pPict, pInjectNow) =>
{
	if (!pPict)
	{
		return false;
	}
	const tmpCSSMap = pPict.CSSMap;
	if (!tmpCSSMap || typeof tmpCSSMap.addCSS !== 'function')
	{
		return false;
	}
	if (!pPict._HeadlightFilterToggleCSSRegistered)
	{
		tmpCSSMap.addCSS('Headlight-FilterToggle-Disabled-CSS', _FILTER_TOGGLE_DISABLED_CSS + _FILTER_TOGGLE_TABULAR_CSS, 500);
		pPict._HeadlightFilterToggleCSSRegistered = true;
	}
	if (pInjectNow && !pPict._HeadlightFilterToggleCSSInjected && typeof tmpCSSMap.injectCSS === 'function')
	{
		tmpCSSMap.injectCSS();
		pPict._HeadlightFilterToggleCSSInjected = true;
	}
	return true;
};

/** Resolve the toggle anchor for a `#ID` selector, or null when it isn't rendered. */
const _getToggleElement = (pPict, pToggleSelector) =>
{
	const tmpAssignment = pPict ? pPict.ContentAssignment : null;
	if (!tmpAssignment || typeof tmpAssignment.getElement !== 'function')
	{
		return null;
	}
	const tmpElements = tmpAssignment.getElement(pToggleSelector);
	const tmpToggle = tmpElements && tmpElements[0];
	return (tmpToggle && typeof tmpToggle.setAttribute === 'function') ? tmpToggle : null;
};

/**
 * Paint the toggle anchor's disabled state. The anchor carries no `href`, so it is not keyboard-focusable
 * and needs no tabindex — `aria-disabled` is what announces it.
 * @param {any} pPict @param {string} pToggleSelector - `#SOME-FILTER-TOGGLE-ID` @param {boolean} pDisabled
 * @return {boolean} true when an anchor was found and painted
 */
const syncToggleDisabledState = (pPict, pToggleSelector, pDisabled) =>
{
	const tmpToggle = _getToggleElement(pPict, pToggleSelector);
	if (!tmpToggle)
	{
		return false;
	}
	ensureFilterToggleCSS(pPict, true);
	if (pDisabled)
	{
		if (tmpToggle.classList)
		{
			tmpToggle.classList.add(DISABLED_CLASS);
		}
		if (!tmpToggle.hasAttribute(TITLE_STASH_ATTRIBUTE))
		{
			tmpToggle.setAttribute(TITLE_STASH_ATTRIBUTE, tmpToggle.getAttribute('title') || '');
		}
		tmpToggle.setAttribute('aria-disabled', 'true');
		tmpToggle.setAttribute('title', DISABLED_TITLE);
		return true;
	}
	if (tmpToggle.classList)
	{
		tmpToggle.classList.remove(DISABLED_CLASS);
	}
	tmpToggle.removeAttribute('aria-disabled');
	if (tmpToggle.hasAttribute(TITLE_STASH_ATTRIBUTE))
	{
		tmpToggle.setAttribute('title', tmpToggle.getAttribute(TITLE_STASH_ATTRIBUTE) || '');
		tmpToggle.removeAttribute(TITLE_STASH_ATTRIBUTE);
	}
	return true;
};

/**
 * The click guard. Read off the rendered anchor rather than the descriptor because the inline `onclick`
 * hands `toggleFilterOverride` only an HTML id — and because both the render-time macro (class) and the
 * init-time sync (aria) mark the same element, so the guard holds before init has run.
 * @param {any} pPict @param {string} pToggleSelector @return {boolean}
 */
const isToggleDisabled = (pPict, pToggleSelector) =>
{
	const tmpToggle = _getToggleElement(pPict, pToggleSelector);
	if (!tmpToggle)
	{
		return false;
	}
	if (typeof tmpToggle.getAttribute === 'function' && tmpToggle.getAttribute('aria-disabled') === 'true')
	{
		return true;
	}
	return !!(tmpToggle.classList && tmpToggle.classList.contains(DISABLED_CLASS));
};

/**
 * Whether the inline "Show All" toggle shows: the descriptor's own `PictForm.ShowAllToggle`, else its
 * specialized-selector config-map entry's (MaterialSelector's top-level-only filter comes with the toggle).
 * @param {Record<string, any>} pInput - the input descriptor (the template macro passes its Record) @return {boolean}
 */
const isToggleVisible = (pInput) =>
{
	const tmpPictForm = pInput && pInput.PictForm ? pInput.PictForm : null;
	if (!tmpPictForm)
	{
		return false;
	}
	return !!(tmpPictForm.ShowAllToggle ?? _SPECIALIZED_SELECTOR_MAP[tmpPictForm.InputType]?.ShowAllToggle);
};

module.exports = { DISABLED_CLASS, DISABLED_TITLE, isInputDisabled, isToggleVisible, ensureFilterToggleCSS, syncToggleDisabledState, isToggleDisabled };
